/**
 * UYAP oturumunun kime ait olduğunun denetimi.
 *
 * Bu bir GÜVENLİK SINIRIDIR. Görevler hesaba bağlıdır; bir avukatın UYAP
 * oturumuyla başkasının işlemleri yapılmamalıdır. Aksi halde bir kullanıcı
 * hesabını başkasına verip, kendi e-imzasıyla açılmış oturum üzerinden
 * işlem yaptırabilir.
 *
 * Üç kimlik de tutmalı: hesap TC == kart TC == oturum TC.
 *
 * Plugin içinde duruyor (uygulamaya özel değil): hem masaüstü imzalayıcı hem
 * icrapro-app aynı UYAP oturumunu paylaşıyor, denetim ikisinde de aynı olmalı.
 * Saf tutulur (store/DOM bağımlılığı yok) ki birim testten geçebilsin.
 */

/** Karşılaştırma için yalnızca rakamları alır. Bilinmeyen değer boş string döner. */
export function tcknNormalize(deger: any): string {
    return String(deger ?? '').replace(/\D/g, '');
}

export interface KimlikDenetimSonucu {
    uyusuyor: boolean;
    /** Uyuşmuyorsa hangi kimliğin çeliştiği — loglama için. */
    sebep?: 'hesap' | 'kart';
    /**
     * Yalnız sıkı modda: uyuşmazlık iki kimliğin ÇELİŞMESİNDEN değil, birinin
     * BİLİNMEMESİNDEN. Çağıran "başka hesabın oturumu" uyarısı göstermemeli.
     */
    bilinmiyor?: boolean;
}

/**
 * Sıkı mod. Varsayılan KAPALI: icrapro-app (tarayıcı) bilinmeyen değeri
 * uyuşmazlık saymaz (DEV-2874). Yalnız imzacı (vue-imza-app) açar: orada hesap
 * TCKN'si lisanstan gelir ve bilinmiyorsa UYAP'a hiçbir şekilde girilmemelidir.
 */
let sikiMod = false;

export function sikiModuAyarla(acik: boolean): void {
    sikiMod = acik === true;
}

export function sikiModAcikMi(): boolean {
    return sikiMod;
}

/**
 * Hesap TCKN'sinin okunacağı kaynak. Varsayılan (kaynak kayıtlı değil): çağıranın
 * verdiği varsayılan okuyucu — plugin içinde sessionStorage 'tckn' (icrapro-app
 * aynı kalır). İmzacı kendi aktif kimliğini (lisans TCKN'si) kaydeder.
 */
let hesapTcknKaynagi: (() => any) | null = null;

export function hesapTcknKaynagiAyarla(fn: (() => any) | null): void {
    hesapTcknKaynagi = fn;
}

/** Kaynak patlarsa değer "bilinmiyor" sayılır (null): sıkı modda kapı kapanır. */
export function hesapTckniOku(varsayilan: () => any): any {
    try {
        return hesapTcknKaynagi ? hesapTcknKaynagi() : varsayilan();
    } catch (e) {
        return null;
    }
}

/**
 * Oturum, hesap ve takılı kart aynı kişiye mi ait?
 *
 * Bilinmeyen (boş) değerler karşılaştırmaya girmez: kimliği doğrulayamamak,
 * uyuşmazlık kanıtı değildir. Bu yüzden eksik veri bağlantıyı düşürmez.
 */
export function oturumKimligiUyusuyor(kimlikler: {
    oturum?: any;
    hesap?: any;
    kart?: any;
}): KimlikDenetimSonucu {
    const oturum = tcknNormalize(kimlikler.oturum);
    const hesap = tcknNormalize(kimlikler.hesap);

    // Sıkı mod: hesap bilinmiyorsa oturumun kime ait olduğu da anlamsızdır.
    if (sikiMod && !hesap) {
        return {uyusuyor: false, sebep: 'hesap', bilinmiyor: true};
    }

    if (!oturum) {
        return {uyusuyor: true};
    }

    if (hesap && hesap !== oturum) {
        return {uyusuyor: false, sebep: 'hesap'};
    }

    // `kart` anahtarı VERİLMEMİŞSE kart denetlenmez (UyapRequest.kimlikKapisi yalnız
    // oturum+hesap verir). Verilmişse sıkı modda boş kart da uyuşmazlıktır.
    const kartVerildi = Object.prototype.hasOwnProperty.call(kimlikler, 'kart');
    const kart = tcknNormalize(kimlikler.kart);
    if (sikiMod && kartVerildi && !kart) {
        return {uyusuyor: false, sebep: 'kart', bilinmiyor: true};
    }
    if (kart && kart !== oturum) {
        return {uyusuyor: false, sebep: 'kart'};
    }

    return {uyusuyor: true};
}

/**
 * Hesabın TC'sine ait kartı seçer.
 *
 * Kart TCKN'si (certificate.subjectSerial) hesap TCKN'siyle eşleşen kart seçilir.
 * Eskiden kart seçimi körlemesine liste[0] idi ve TCKN yalnızca giriş anında
 * REDDETMEK için okunuyordu; çoklu kartta doğru kart elde olmasına rağmen
 * "İzinsiz İmza" alınıyordu.
 *
 * Eşleşme yoksa: tek kart varsa belirsizlik de yok, o seçilir. Birden fazla kart
 * varken rastgele seçmek başka bir avukatın kartıyla giriş demek — null döner ve
 * seçim kullanıcıya bırakılır.
 */
export function hesabaAitKartiSec(kartlar: any[], hesapTcknHam: any): string | null {
    if (!Array.isArray(kartlar) || kartlar.length === 0) {
        return null;
    }
    const hesapTckn = tcknNormalize(hesapTcknHam);
    // Sıkı mod: hesap bilinmeden kart seçilmez (tek kart bile).
    if (sikiMod && !hesapTckn) {
        return null;
    }
    if (hesapTckn) {
        const eslesen = kartlar.find(
            (kart: any) => tcknNormalize(kart?.certificate?.subjectSerial) === hesapTckn
        );
        if (eslesen) {
            return eslesen?.certificate?.certificateId ?? null;
        }
    }
    if (kartlar.length === 1) {
        return kartlar[0]?.certificate?.certificateId ?? null;
    }
    return null;
}

/** Sıkı modda hesap TCKN'si bilinmediği için istek durdurulduğunda (uyuşmazlık değil, eksiklik). */
export const HESAP_TCKN_BILINMIYOR_MESAJI =
    'Hesabınıza ait TC kimlik numarası bilinmediği için UYAP işlemi yapılamaz. ' +
    'Bu büronun eİcraPro lisansında TC kimlik numarası kayıtlı olmalıdır; destek ile iletişime geçin.';

export const KIMLIK_UYUSMAZLIGI_MESAJI =
    'Açık olan UYAP oturumu bu hesaba ait değil. Hesabınızda kayıtlı TC kimlik ' +
    'numarası ile UYAP oturumundaki TC kimlik numarası farklı olduğu için işlem ' +
    'yapılamaz. Kendi hesabınızla giriş yapıp kendi e-imzanızı kullanın.';

/**
 * Kimlik kapısının (UyapRequest.kimlikKapisi) fırlattığı Error'un `kod` alanı:
 * 'KIMLIK_UYUSMAZLIGI' (çatışma) ve 'HESAP_TCKN_BILINMIYOR' (eksiklik). Görev
 * yürütücüsü bu iki kodda görevi BITTI_HATALI yapmaz: görev başarısız değildir,
 * bot durdurulur ve görev kaldığı yerden sürer.
 */
export type KimlikHataKodu = 'KIMLIK_UYUSMAZLIGI' | 'HESAP_TCKN_BILINMIYOR';

export function kimlikHatasi(mesaj: string, kod: KimlikHataKodu): Error {
    const hata: any = new Error(mesaj);
    hata.kod = kod;
    return hata;
}

/** Hata kimlik kapısından mı (uyuşmazlık ya da hesap TCKN'si bilinmiyor)? */
export function kimlikKapisiHatasiMi(hata: any): boolean {
    return !!hata && (hata.kod === 'KIMLIK_UYUSMAZLIGI' || hata.kod === 'HESAP_TCKN_BILINMIYOR');
}

/**
 * Kimlik kapısının SON hatası (bayrak). UyapRequest.kimlikKapisi fırlatmadan hemen
 * önce kaydeder; TaskHandler.handle görevden önce temizler, görevden sonra okur.
 *
 * Neden bayrak: görev yürütücüleri hatayı `new Error(e.message)` ile kodsuz sarıyor,
 * bazıları ise (TakipSorgula iç catch'leri) hiç fırlatmayıp BITTI_EKSIK'e çeviriyor;
 * `kod` TaskHandler'a ulaşmıyor. Bayrak 17 yürütücüye dokunmadan her yolu kapsar.
 * Bayrak kimlikHatasi()'nde DEĞİL kapıda kurulur: o üretici vue store'un bağlanma
 * akışında da kullanılıyor, orada görev yok.
 */
let sonKimlikKapisiHatasi: Error | null = null;

/** Kapı hatasını bayrağa yazar ve aynı hatayı döndürür (`throw kimlikKapisiHatasiniKaydet(...)`). */
export function kimlikKapisiHatasiniKaydet(hata: Error): Error {
    sonKimlikKapisiHatasi = hata;
    return hata;
}

/** Bayraktaki kapı hatası (yoksa null). Okur, temizlemez. */
export function kimlikKapisiHatasiniAl(): Error | null {
    return sonKimlikKapisiHatasi;
}

export function kimlikKapisiBayraginiTemizle(): void {
    sonKimlikKapisiHatasi = null;
}

/**
 * Kimlik uyuşmazlığı tespit edildiğinde çağrılacak kanca (engelleyici dialog +
 * botu durdurma). Kota/devre kesici kancalarıyla aynı desen: uygulama kaydeder,
 * kaydedilmezse sessizce atlanır — böylece bu modül store/DOM'a bağlanmaz.
 */
let kimlikUyusmazligiKancasi: ((sebep: 'hesap' | 'kart') => void) | null = null;

export function kimlikUyusmazligiKancasiniAyarla(
    fn: ((sebep: 'hesap' | 'kart') => void) | null
): void {
    kimlikUyusmazligiKancasi = fn;
}

export function kimlikUyusmazligiBildir(sebep: 'hesap' | 'kart'): void {
    if (!kimlikUyusmazligiKancasi) {
        return;
    }
    try {
        kimlikUyusmazligiKancasi(sebep);
    } catch (e) {
        console.warn('Kimlik uyuşmazlığı uyarısı gösterilemedi:', e);
    }
}
